/**
 * The tab's audio engine: what plays, when it changes, and how it changes — plan §9.4, §9.5, §9.9.
 *
 * WHAT IT DECIDES, AND WHAT IT DOES NOT. Every decision about WHICH area and WHERE on a timeline is a
 * pure function in `@dimensionscore/shared/audioplay`; this class only applies them: it holds the
 * catalog and the latest context, asks `choosePlaybacks`, and — when a layer's answer's key changes —
 * crossfades that layer from what is playing to what should be. The sound itself comes from an {@link
 * AudioBackend}: Web Audio in a browser (`webaudio.ts`), a recording fake in the unit tests. So the
 * engine is testable in jsdom with no AudioContext at all, and the Web Audio half is exercised by the
 * e2e suite in a real (headless) Chromium.
 *
 * TWO LAYERS, ONE SET OF RULES. The tab plays two areas at once: the MAIN layer — the soundtrack, shared
 * instances and all — and the VEHICLE layer, the sound of the ride the player sits on, chosen among `vehicle`
 * areas alone and played over the main one on a bus of its own (`vehicle`: its own slider, under master).
 * Each is a {@link PlaybackLayer} — the same state machine, holding its own playback, its own leaving and
 * fading decks and its own timers — told its own choice. So a battle, a shared instance or a new zone moves
 * the main layer and leaves the vehicle one exactly where it is, and a car pulling away moves the vehicle
 * layer and leaves the music alone. What the layers share is the backend, the manifest and the clock.
 *
 * THE RULES EACH LAYER HOLDS:
 *
 *   * A RESYNC DOES NOT RESTART (§9.4). A context, a catalog or a reconnect that resolves to the same
 *     playback key changes gains at most. Nothing else is touched.
 *   * A BLIP IS INAUDIBLE. Offline lingers {@link AUDIO_OFFLINE_LINGER_MS} before it fades: the sidecar
 *     marks everyone offline the instant the game server's socket drops (plan §9.3 "Wire rules"), and
 *     the mod's re-sent context lands well inside the linger, on the same key, so nothing happened.
 *   * A SHARED INSTANCE SEEKS (§9.9). Its playback starts at `timelineAt(correctedNow − startedAtMs)`,
 *     re-derives the next track from the timeline rather than from "what came next", and nudges the
 *     playback rate on every tick to stay on the beat — a hard re-seek only past a threshold. (Only the main
 *     layer ever has one: an instance does not reach the vehicle layer.)
 *   * EVERY CROSSFADE IS SCHEDULED ON THE AUDIO THREAD (gain ramps), never by a timer, because a hidden
 *     tab's timers run at 4 Hz at best (the Phase 0 spike) and a fade built on them would stutter.
 *   * A CROSSFADE'S TWO HALVES START TOGETHER, AND KEEP THE POWER UP (owner: "music mixing is very
 *     hearable, it's not a smooth transition"). Measured before this rule, with steady noise in place of
 *     the music so that only the engine's gains show: the outgoing area began fading the instant the
 *     context changed, but the incoming one only once its buffer had been fetched and decoded — half a
 *     second later for a cached hub track — and both fades were linear in gain, so the mix sank 5 dB
 *     under a smooth line between two districts and 6 dB on the way into a battle. Now the outgoing playback
 *     holds at full level ("leaving") until the incoming deck actually starts, and both fades are then
 *     scheduled for the same audio-clock instant over the same length, along complementary quarter-sine
 *     curves (the deck's `fade`), so the summed power stays level through the change. If the incoming
 *     never starts (a failed load, a fetch stuck past {@link HANDOVER_MAX_WAIT_MS}), the outgoing fades on
 *     its own rather than playing the wrong area for ever.
 *   * THE LENGTH IS THE AREA'S OWN `fadeMs`, both halves alike — what `audio.yml` authors per area, and
 *     the shipped defaults are already the right shape: 3 s into an ambient dimension, 2.5 s between Nexus
 *     districts, 2 s into a base or the Tower climb, 0.8 s into a battle (quick, because a battle starts
 *     NOW, and smooth because the curves are), and the area's own on the way back out, under the
 *     victory sting's duck. Nothing clamps it any more. A layer left with nothing to play fades to silence
 *     over the area it is leaving — which is how the vehicle layer goes quiet when the player gets off.
 *   * A TRACK BOTH AREAS PLAY IS NOT RESTARTED: the incoming playback takes the deck over where it is, and
 *     only the level moves to the new area's.
 */
import {
  AUDIO_OFFLINE_LINGER_MS,
  choosePlaybacks,
  cueCategoryOf,
  instanceSeed,
  loudnessGain,
  loudnessKindOf,
  playOrderForRound,
  seedOf,
  sliderGain,
  timelineAt,
  trackFile,
  volumeCategoryOf,
  type AreaDef,
  type AudioCatalog,
  type AudioContextMessage,
  type AudioFormat,
  type AudioInstanceRef,
  type AudioManifest,
  type AudioManifestEntry,
  type AudioVolumes,
  type PlaybackChoice,
} from '@dimensionscore/shared';

/**
 * The buses a sound is mixed into. `music` carries ambient and music areas (§9.5), `battle` battle areas,
 * `vehicle` the vehicle layer and the vehicle cues.
 */
export type AudioBus = 'music' | 'battle' | 'vehicle' | 'alerts' | 'voice';

/** One playing file — a decoded track through its gains, as the backend builds it. */
export interface Deck {
  /** Loads the file; resolves once it can play. Rejects with {@link TrackMissingError} on a 404. */
  load(url: string): Promise<void>;
  /** Milliseconds, once loaded. */
  readonly durationMs: number | null;
  /**
   * Starts so that `offsetMs` is what is HEARD now. `loop` repeats the whole file end to end, gaplessly:
   * the library's loops have no loop points (plan §9.7), the last sample flows into the first.
   */
  play(offsetMs: number, loop: boolean): Promise<void>;
  /** Where in the file the listener is now, in milliseconds. */
  positionMs(): number;
  seek(offsetMs: number): void;
  setRate(rate: number): void;
  /**
   * The deck's own level — a track's loudness correction times its area's volume. Immediate, or ramped
   * over `rampMs` (a carried-over track moving to its new area's volume).
   */
  setLevel(level: number, rampMs?: number): void;
  /**
   * Moves the deck's fade gain to `to` (0 or 1) over `ms`, on the audio thread, along a quarter sine:
   * the equal-power curve, so a fade-in and a fade-out of the same length started together sum to a
   * constant power. Asking again for the target it is already heading to changes nothing.
   */
  fade(to: number, ms: number): void;
  /** The file played to its end (not called while `loop` is on). */
  onEnded(listener: () => void): void;
  /** A tick while playing — a few a second in a visible tab, about one a second in a hidden one. */
  onTick(listener: () => void): void;
  dispose(): void;
}

/** What the engine needs from whatever makes the noise. */
export interface AudioBackend {
  /** Starts (or resumes) audio. Must be called from a user gesture the first time (§6.1). */
  resume(): Promise<void>;
  /** A deck on one of the buses areas play on: `music`, `battle`, `vehicle` (and `voice`, reserved). */
  createDeck(bus: Exclude<AudioBus, 'alerts'>): Deck;
  /** A one-shot: a cue under `alerts` or `vehicle` (its `CueDef.category`), a voiced line under `voice`. */
  playOneShot(url: string, bus: 'alerts' | 'voice' | 'vehicle', level: number): Promise<void>;
  setBusGain(bus: 'master' | AudioBus, gain: number): void;
  /** Lowers the music and battle buses under a one-shot, as {@link duckFor} shapes it. The vehicle bus is never ducked. */
  duck(duck: Duck): void;
  /** The mix as frequency bins, for the visualiser. Null when the backend has nothing to show yet. */
  readonly analyser?: { readonly frequencyBinCount: number; getByteFrequencyData(array: Uint8Array<ArrayBuffer>): void } | null;
  /** Milliseconds a fade lasts at most before a deck is released, so a fade never leaks a deck. */
  dispose(): void;
}

/** How the music makes room for a one-shot: how far it dips, for how long, and how it comes back. */
export interface Duck {
  /** Milliseconds at the dipped level. */
  readonly holdMs: number;
  /** The music and battle buses' gain while dipped. */
  readonly depth: number;
  /** Milliseconds to swell back to full. */
  readonly releaseMs: number;
}

/** A one-shot at least this long is a STING, and the music gets out of its way rather than ducking for a blip. */
export const STING_MIN_MS = 2_000;

/**
 * The duck for a one-shot of `durationMs`. A cue is a blip: the music dips a little and is back within
 * half a second. A sting is a moment — the victory fanfare is 11.7 s, and the area music is fading back
 * in under it as the battle ends — so the music sits well under it for the whole of it and swells back
 * afterwards, rather than two pieces playing over each other.
 */
export function duckFor(durationMs: number): Duck {
  const length = Number.isFinite(durationMs) && durationMs > 0 ? durationMs : 800;
  if (length >= STING_MIN_MS) return { holdMs: length, depth: 0.3, releaseMs: 1_500 };
  return { holdMs: Math.max(400, length), depth: 0.55, releaseMs: 300 };
}

/**
 * A looping track's drift, read the short way round its seam: a deck 50 ms before the end of the file
 * against a timeline 30 ms past the top is 80 ms behind, not a whole track ahead.
 */
export function wrappedDrift(driftMs: number, loopMs: number | null): number {
  if (loopMs === null || !(loopMs > 0)) return driftMs;
  const half = loopMs / 2;
  return ((((driftMs + half) % loopMs) + loopMs) % loopMs) - half;
}

/** A track the deploy no longer has — this tab is older than the site. */
export class TrackMissingError extends Error {
  constructor(readonly url: string) {
    super(`${url} is not on this deploy`);
    this.name = 'TrackMissingError';
  }
}

/** What the page draws of one layer: the playback it is in, and the track it is on. */
export interface LayerSnapshot {
  readonly state: 'idle' | 'playing' | 'silent' | 'finished' | 'lingering';
  readonly area: AreaDef | null;
  /** The shared playback deciding this layer, if any — only ever on the main layer. */
  readonly instance: AudioInstanceRef | null;
  readonly trackId: string | null;
  readonly track: AudioManifestEntry | null;
  /** How many times a playback has STARTED on this layer since the engine was built. A resync must not move it. */
  readonly starts: number;
}

/** What the page draws: the main layer, as it always has, and the vehicle layer beside it. */
export interface EngineSnapshot extends LayerSnapshot {
  readonly vehicle: LayerSnapshot;
}

const IDLE_LAYER: LayerSnapshot = { state: 'idle', area: null, instance: null, trackId: null, track: null, starts: 0 };

/** An engine that has played nothing on either layer. */
export const IDLE_SNAPSHOT: EngineSnapshot = { ...IDLE_LAYER, vehicle: IDLE_LAYER };

/** One tick's report of where a deck is — what the page shows, and what the e2e suite reads. */
export interface TickSample {
  readonly positionMs: number;
  readonly expectedMs: number | null;
  readonly atMs: number;
  readonly durationMs: number | null;
}

export interface EngineEvents {
  readonly onChange?: (snapshot: EngineSnapshot) => void;
  /** An `alerts` cue landed — the page says so. A vehicle cue is the ride's own sound, not news, and is not reported. */
  readonly onCue?: (cueId: string) => void;
  /** A hashed file 404'd: the page is older than the deploy, and the remedy is a reload. */
  readonly onStale?: () => void;
  /** A tick on the MAIN layer's playing deck, for anything that displays a position. */
  readonly onTick?: (sample: TickSample) => void;
}

export interface EngineOptions {
  readonly backend: AudioBackend;
  readonly manifest: AudioManifest;
  readonly format: AudioFormat;
  /** The sidecar's clock as this tab estimates it (`AudioClock.serverNow(Date.now())`). */
  readonly serverNow: () => number;
  /** Local epoch milliseconds. */
  readonly now?: () => number;
  readonly random?: () => number;
  readonly events?: EngineEvents;
  /** Injected by tests; the real timer otherwise. Used for the offline linger and the handover's patience. */
  readonly setTimer?: (callback: () => void, ms: number) => unknown;
  readonly clearTimer?: (handle: unknown) => void;
}

/**
 * The longest the outgoing music waits, at full level, for the incoming deck to start. Past it the
 * outgoing fades on its own: a fetch that hangs (the backend gives up at 30 s) must not keep the area
 * the player has left playing for that long. Comfortably above a cold fetch-and-decode of a track.
 */
export const HANDOVER_MAX_WAIT_MS = 3_000;

/** Past this much drift a shared playback re-seeks; under it the rate is nudged. */
export const INSTANCE_RESEEK_MS = 250;
/** Under this much drift nothing is corrected — the clock's own error is about this size. */
export const INSTANCE_TOLERANCE_MS = 25;
/** The largest rate change a nudge makes: ±3 % is inaudible on music and closes 100 ms in ~3 s. */
export const INSTANCE_MAX_RATE_NUDGE = 0.03;

export class AudioEngine {
  private readonly backend: AudioBackend;
  private readonly manifest: AudioManifest;
  private readonly format: AudioFormat;
  private readonly events: EngineEvents;

  private catalog: AudioCatalog | null = null;
  private context: AudioContextMessage | null = null;
  /** The soundtrack: every area but the vehicle ones, shared instances outranking them all. */
  private readonly main: PlaybackLayer;
  /** The ride the player sits on: vehicle areas alone, over the main layer, on the vehicle bus. */
  private readonly vehicle: PlaybackLayer;
  private volumes: AudioVolumes | null = null;
  private muted = false;
  private enabled = false;
  private stale = false;

  constructor(options: EngineOptions) {
    this.backend = options.backend;
    this.manifest = options.manifest;
    this.format = options.format;
    this.events = options.events ?? {};
    const host: LayerHost = {
      backend: this.backend,
      manifest: this.manifest,
      format: this.format,
      serverNow: options.serverNow,
      now: options.now ?? (() => Date.now()),
      random: options.random ?? Math.random,
      setTimer: options.setTimer ?? ((callback, ms) => setTimeout(callback, ms)),
      clearTimer: options.clearTimer ?? ((handle) => clearTimeout(handle as ReturnType<typeof setTimeout>)),
      enabled: () => this.enabled,
      emit: () => this.emit(),
      loadFailed: (error) => this.onLoadFailure(error),
    };
    // Only the main layer's position is reported: the page shows one, and it is the soundtrack's.
    this.main = new PlaybackLayer(host, (sample) => this.events.onTick?.(sample));
    this.vehicle = new PlaybackLayer(host, null);
  }

  /** The user pressed "Enable audio": start the backend and play whatever the context says. */
  async enable(): Promise<void> {
    await this.backend.resume();
    this.enabled = true;
    this.applyVolumes();
    this.reconcile();
  }

  get isEnabled(): boolean {
    return this.enabled;
  }

  /** The backend's analyser, once audio has started. */
  get analyser(): AudioBackend['analyser'] {
    return this.backend.analyser ?? null;
  }

  setCatalog(catalog: AudioCatalog): void {
    this.catalog = catalog;
    this.reconcile();
  }

  setContext(message: AudioContextMessage): void {
    this.context = message;
    this.reconcile();
  }

  setVolumes(volumes: AudioVolumes, muted: boolean): void {
    this.volumes = volumes;
    this.muted = muted;
    this.applyVolumes();
  }

  /**
   * A one-shot from the mod (§9.4 `CueDef`), under the slider of its category. Unknown ids, and cues before
   * "enable", are dropped.
   *
   * AN ALERT MAKES ROOM FOR ITSELF AND IS SAID ON THE PAGE; A VEHICLE CUE DOES NEITHER. A mail chime is news,
   * so the music ducks under it and the phone shows it. A vehicle cue is the ride's own sound — a car setting
   * off, stopping, splashing, as often as a tour stops — so it is mixed in with the vehicle layer it belongs to:
   * no duck (the music would pump at every stop) and no notification (nobody needs telling the car moved).
   */
  cue(cueId: string): void {
    const cue = this.catalog?.cues.find((candidate) => candidate.id === cueId);
    const category = cue === undefined ? 'alerts' : cueCategoryOf(cue);
    if (category === 'alerts') this.events.onCue?.(cueId);
    if (!this.enabled || cue === undefined) return;
    const entry = this.manifest[cue.track];
    if (entry === undefined) return;
    const url = trackFile(entry, this.format);
    if (url === null) return;
    if (category === 'alerts') this.backend.duck(duckFor(entry.durationMs));
    this.backend.playOneShot(url, category, cue.volume * loudnessGain(entry.loudnessLufs, loudnessKindOf(cue.track))).catch((error: unknown) => this.onLoadFailure(error));
  }

  snapshot(): EngineSnapshot {
    return { ...this.main.snapshot(), vehicle: this.vehicle.snapshot() };
  }

  /** Where the main layer's playing deck is, and where the instance timeline says it should be. */
  position(): { readonly positionMs: number; readonly expectedMs: number | null } | null {
    return this.main.position();
  }

  /** Stops everything and releases every deck. */
  dispose(): void {
    this.main.dispose();
    this.vehicle.dispose();
    this.backend.dispose();
  }

  // ------------------------------------------------------------------ the decision

  /** Both layers, each told its own choice — neither's ever moves the other's (`choosePlaybacks`). */
  private reconcile(): void {
    const { main, vehicle } = choosePlaybacks(this.catalog, this.context);
    const offline = this.context !== null && !this.context.online;
    this.main.reconcile(main, offline);
    this.vehicle.reconcile(vehicle, offline);
  }

  private applyVolumes(): void {
    const volumes = this.volumes;
    if (volumes === null) return;
    this.backend.setBusGain('master', this.muted ? 0 : sliderGain(volumes.master));
    this.backend.setBusGain('music', sliderGain(volumes.music));
    this.backend.setBusGain('battle', sliderGain(volumes.battle));
    this.backend.setBusGain('vehicle', sliderGain(volumes.vehicle));
    this.backend.setBusGain('alerts', sliderGain(volumes.alerts));
    this.backend.setBusGain('voice', sliderGain(volumes.voice));
  }

  private onLoadFailure(error: unknown): void {
    if (error instanceof TrackMissingError) {
      if (!this.stale) {
        this.stale = true;
        this.events.onStale?.();
      }
      return;
    }
    console.warn(`audio: a track could not play: ${error instanceof Error ? error.message : String(error)}`);
  }

  private emit(): void {
    this.events.onChange?.(this.snapshot());
  }
}

// ---------------------------------------------------------------------- one layer

/** What a layer needs from the engine that owns it — the same for both layers. */
interface LayerHost {
  readonly backend: AudioBackend;
  readonly manifest: AudioManifest;
  readonly format: AudioFormat;
  readonly serverNow: () => number;
  readonly now: () => number;
  readonly random: () => number;
  readonly setTimer: (callback: () => void, ms: number) => unknown;
  readonly clearTimer: (handle: unknown) => void;
  /** Whether "Enable audio" has been pressed: nothing makes a sound before it. */
  readonly enabled: () => boolean;
  /** Something the page draws changed. */
  readonly emit: () => void;
  readonly loadFailed: (error: unknown) => void;
}

/** One playback — an area, or an instance of one — and the deck it is on. */
interface Playback {
  readonly choice: PlaybackChoice;
  deck: Deck | null;
  trackId: string | null;
  /** Personal playback's place in its play order (instances re-derive theirs from the timeline). */
  round: number;
  slot: number;
  readonly seed: number;
  /** Local instant a personal playback started; an instance's start is its `startedAtMs`. */
  readonly startedLocalMs: number;
  finished: boolean;
  disposed: boolean;
  /** Whether a deck has been asked for — false for a playback chosen before "Enable audio". */
  started: boolean;
  /** A track change is in flight; ticks from the old deck must not start a second one. */
  loading: boolean;
}

/**
 * ONE LAYER'S STATE MACHINE: the playback it is on, the ones still leaving or fading, the handover's patience
 * and the offline linger — every rule in the file note, for whichever layer this is. It is told its choice
 * ({@link reconcile}); it never chooses. Its decks go on the bus of the area they play (`volumeCategoryOf`), so
 * the main layer's land on `music` or `battle` and the vehicle layer's on `vehicle` with no layer-specific code.
 */
class PlaybackLayer {
  private readonly host: LayerHost;
  /** Reports the playing deck's position on each tick; null for a layer whose position nobody shows. */
  private readonly ticked: ((sample: TickSample) => void) | null;
  private current: Playback | null = null;
  private readonly fading = new Set<Playback>();
  /** Still at full level, waiting for the playback that replaces them to start (see the file note). */
  private readonly leaving = new Set<Playback>();
  private handoverTimer: unknown = null;
  private lingerTimer: unknown = null;
  private starts = 0;

  constructor(host: LayerHost, ticked: ((sample: TickSample) => void) | null) {
    this.host = host;
    this.ticked = ticked;
  }

  snapshot(): LayerSnapshot {
    const playback = this.current;
    const lingering = this.lingerTimer !== null;
    const state: LayerSnapshot['state'] =
      playback === null || !playback.started
        ? 'idle'
        : lingering
          ? 'lingering'
          : playback.finished
            ? 'finished'
            : playback.deck === null
              ? 'silent'
              : 'playing';
    const trackId = playback?.trackId ?? null;
    return {
      state,
      area: playback?.choice.area ?? null,
      instance: playback?.choice.instance ?? null,
      trackId,
      track: trackId === null ? null : (this.host.manifest[trackId] ?? null),
      starts: this.starts,
    };
  }

  /** Where the playing deck is, and where the instance timeline says it should be. */
  position(): { readonly positionMs: number; readonly expectedMs: number | null } | null {
    const playback = this.current;
    if (playback?.deck == null) return null;
    return { positionMs: playback.deck.positionMs(), expectedMs: this.expectedOffset(playback) };
  }

  /** Stops this layer and releases every deck it holds. */
  dispose(): void {
    this.cancelLinger();
    this.cancelHandover();
    for (const playback of [this.current, ...this.fading, ...this.leaving]) if (playback !== null) this.release(playback);
    this.current = null;
    this.fading.clear();
    this.leaving.clear();
  }

  // ------------------------------------------------------------------ the decision

  /** This layer's choice (null: nothing should play here), and whether the player is offline. */
  reconcile(choice: PlaybackChoice | null, offline: boolean): void {
    const current = this.current;

    if (choice !== null && current !== null && choice.key === current.choice.key) {
      // THE SAME PLAYBACK. A resync, a repeated context, a reconnect: nothing restarts. Only the
      // level can move (a config reload that turned the area down keeps its key).
      this.cancelLinger();
      if (!current.started && this.host.enabled()) {
        // Chosen before the gesture, audible now: this is its first start, not a restart.
        this.begin(current, choice.area.fadeMs);
        return;
      }
      // Idempotent on a deck already heading to full (a resync mid-crossfade leaves the curve alone), so
      // no shortened fade is needed here: the area's own length is right if it is needed at all.
      current.deck?.fade(1, choice.area.fadeMs);
      if (current.trackId !== null) current.deck?.setLevel(this.levelOf(choice.area, current.trackId));
      this.host.emit();
      return;
    }

    if (choice === null) {
      if (current === null) return this.host.emit();
      if (offline) {
        // THE LINGER: offline may be half of an offline-then-online pair (a game-server blip).
        if (this.lingerTimer === null) {
          this.lingerTimer = this.host.setTimer(() => {
            this.lingerTimer = null;
            this.handOver(current.choice.area.fadeMs);
            this.fadeOut(current, current.choice.area.fadeMs);
            this.current = null;
            this.host.emit();
          }, AUDIO_OFFLINE_LINGER_MS);
        }
        return this.host.emit();
      }
      // Nothing comes next, so nothing is waited for: everything still sounding fades now, together.
      this.handOver(current.choice.area.fadeMs);
      this.fadeOut(current, current.choice.area.fadeMs);
      this.current = null;
      return this.host.emit();
    }

    this.cancelLinger();
    if (!this.host.enabled()) {
      // Not audible yet (no gesture): remember the choice so the page can show it, play nothing.
      this.current = this.newPlayback(choice);
      return this.host.emit();
    }
    // THE SAME TRACK, CARRIED ON: the area now chosen plays the track already playing, so the deck is
    // handed over where it is and only its level moves — no restart, no crossfade into itself.
    if (current !== null) {
      const carried = this.carryOver(current, choice);
      if (carried !== null) {
        this.current = carried;
        return this.host.emit();
      }
    }
    // A playback that comes straight back (a quick out-and-in) is revived rather than restarted: it is
    // the same key, still loaded, still in place — still at full level if it was only leaving, or fading,
    // in which case it fades back in while what replaced it fades out, both now and together.
    const back = (playback: Playback): boolean => playback.choice.key === choice.key && playback.deck !== null;
    const waiting = [...this.leaving].find(back);
    const revived = waiting ?? [...this.fading].find(back);
    if (revived !== undefined) {
      this.leaving.delete(revived);
      this.fading.delete(revived);
      revived.deck?.fade(1, choice.area.fadeMs);
      if (current !== null && current !== revived) this.leave(current);
      this.handOver(choice.area.fadeMs);
      this.current = revived;
      return this.host.emit();
    }
    if (current !== null) this.leave(current);
    const playback = this.newPlayback(choice);
    this.current = playback;
    this.begin(playback, choice.area.fadeMs);
  }

  /**
   * `playback` is being replaced. If it never made a sound (still loading, or chosen before the gesture)
   * it goes now; otherwise it keeps playing at full level until the replacement starts (`handOver`), or
   * until {@link HANDOVER_MAX_WAIT_MS} has passed without one.
   */
  private leave(playback: Playback): void {
    if (playback.deck === null) {
      this.release(playback);
      return;
    }
    this.leaving.add(playback);
    if (this.handoverTimer === null) {
      this.handoverTimer = this.host.setTimer(() => {
        this.handoverTimer = null;
        this.handOver(this.current?.choice.area.fadeMs ?? playback.choice.area.fadeMs);
      }, HANDOVER_MAX_WAIT_MS);
    }
  }

  /** The other half of a crossfade: everything leaving fades now, over `ms` — the incoming half's length. */
  private handOver(ms: number): void {
    this.cancelHandover();
    for (const playback of this.leaving) this.fadeOut(playback, ms);
    this.leaving.clear();
  }

  private cancelHandover(): void {
    if (this.handoverTimer !== null) {
      this.host.clearTimer(this.handoverTimer);
      this.handoverTimer = null;
    }
  }

  /**
   * A playback for `choice` that takes `from`'s deck over, when `choice`'s area plays the very track
   * `from` is playing: personal playback only (an instance's timeline decides its own position), and only
   * when both loop or neither does (a deck's loop is fixed when it starts).
   */
  private carryOver(from: Playback, choice: PlaybackChoice): Playback | null {
    const deck = from.deck;
    const trackId = from.trackId;
    if (deck === null || trackId === null || from.loading || from.choice.instance !== null || choice.instance !== null) return null;
    const playable = this.playable(choice.area);
    const index = playable.indexOf(trackId);
    if (index < 0 || (from.choice.area.order === 'loop') !== (choice.area.order === 'loop')) return null;
    const next = this.newPlayback(choice);
    const order = playOrderForRound(choice.area.order, playable.length, 0, next.seed);
    next.slot = Math.max(0, order.indexOf(index));
    next.deck = deck;
    next.trackId = trackId;
    next.started = true;
    // The old playback no longer owns the deck: releasing it later must not stop the music.
    from.deck = null;
    from.disposed = true;
    deck.onEnded(() => this.advance(next, deck));
    deck.onTick(() => this.tick(next, deck));
    deck.setLevel(this.levelOf(choice.area, trackId), choice.area.fadeMs);
    deck.fade(1, choice.area.fadeMs);
    // Anything still leaving from an earlier change has nothing to wait for now.
    this.handOver(choice.area.fadeMs);
    return next;
  }

  private begin(playback: Playback, fadeInMs: number): void {
    playback.started = true;
    this.starts += 1;
    this.host.emit();
    void this.startTrack(playback, fadeInMs);
  }

  private newPlayback(choice: PlaybackChoice): Playback {
    const seed = choice.instance === null ? seedOf(`${choice.key}:${Math.floor(this.host.random() * 2 ** 31)}`) : instanceSeed(choice.instance);
    return { choice, deck: null, trackId: null, round: 0, slot: 0, seed, startedLocalMs: this.host.now(), finished: false, disposed: false, started: false, loading: false };
  }

  /** The area's tracks this deploy can actually play, in catalog order. */
  private playable(area: AreaDef): string[] {
    return area.tracks.filter((id) => {
      const entry = this.host.manifest[id];
      return entry !== undefined && trackFile(entry, this.host.format) !== null;
    });
  }

  // ------------------------------------------------------------------ the decks

  /** Which track, and where in it, a playback should be on right now. */
  private placement(playback: Playback): { trackId: string; offsetMs: number } | 'finished' | null {
    const { area, instance } = playback.choice;
    const playable = this.playable(area);
    if (playable.length === 0) return null;
    if (instance !== null) {
      const timeline = playable.map((id) => ({ id, durationMs: this.host.manifest[id]?.durationMs ?? 0 }));
      const point = timelineAt(area.order, timeline, this.host.serverNow() - instance.startedAtMs, playback.seed);
      if (point.kind === 'finished') return 'finished';
      if (point.kind === 'playing') return { trackId: point.trackId, offsetMs: point.offsetMs };
      // No durations to seek with: start at the top of the first track rather than not at all.
      return { trackId: playable[0] as string, offsetMs: 0 };
    }
    // Personal playback walks its own order and starts each track at the top.
    if (area.order === 'once' && playback.round > 0) return 'finished';
    const order = playOrderForRound(area.order, playable.length, playback.round, playback.seed);
    const index = order[playback.slot] ?? 0;
    return { trackId: playable[index] as string, offsetMs: 0 };
  }

  private async startTrack(playback: Playback, fadeInMs: number): Promise<void> {
    if (playback.disposed || playback.loading) return;
    playback.loading = true;
    try {
      await this.loadAndPlay(playback, fadeInMs);
    } finally {
      playback.loading = false;
    }
  }

  private async loadAndPlay(playback: Playback, fadeInMs: number): Promise<void> {
    const placement = this.placement(playback);
    if (placement === null) {
      playback.trackId = null;
      // Nothing to play here, so nothing to wait for: what is leaving goes now.
      if (playback === this.current) this.handOver(playback.choice.area.fadeMs);
      return this.host.emit();
    }
    if (placement === 'finished') {
      playback.finished = true;
      playback.deck?.fade(0, 400);
      if (playback === this.current) this.handOver(playback.choice.area.fadeMs);
      return this.host.emit();
    }
    const entry = this.host.manifest[placement.trackId] as AudioManifestEntry;
    const url = trackFile(entry, this.host.format) as string;
    // The area's own bus: music or battle on the main layer, vehicle on the vehicle layer.
    const deck = this.host.backend.createDeck(volumeCategoryOf(playback.choice.area));
    deck.setLevel(this.levelOf(playback.choice.area, placement.trackId));
    deck.fade(0, 0);
    try {
      await deck.load(url);
    } catch (error) {
      deck.dispose();
      // The incoming half never comes, so the outgoing half does not wait for it.
      if (playback === this.current) this.handOver(playback.choice.area.fadeMs);
      this.host.loadFailed(error);
      return;
    }
    if (playback.disposed) {
      deck.dispose();
      return;
    }
    const previous = playback.deck;
    playback.deck = deck;
    playback.trackId = placement.trackId;
    const loop = playback.choice.area.order === 'loop';
    deck.onEnded(() => this.advance(playback, deck));
    deck.onTick(() => this.tick(playback, deck));
    // An instance re-reads the timeline AFTER the load, so a slow fetch costs no sync.
    const offset = playback.choice.instance === null ? placement.offsetMs : this.expectedOffset(playback) ?? placement.offsetMs;
    try {
      await deck.play(offset, loop);
    } catch (error) {
      if (playback === this.current) this.handOver(playback.choice.area.fadeMs);
      this.host.loadFailed(error);
      return;
    }
    deck.fade(1, fadeInMs);
    // THE CROSSFADE'S OTHER HALF, on this same instant and over the same length — not when the area
    // changed, which was before this buffer could play (see the file note).
    if (playback === this.current) this.handOver(fadeInMs);
    if (previous !== null && previous !== deck) {
      previous.fade(0, 120);
      this.releaseLater(previous, 200);
    }
    this.host.emit();
  }

  /** The playing file ended: on to whatever the order (or the timeline) says comes next. */
  private advance(playback: Playback, deck: Deck): void {
    if (playback.disposed || playback.deck !== deck) return;
    // A file that ends while its area is on the way out (leaving or fading) is not followed by the next
    // one — that would start the area the player has left again. The deck is silent now; it just goes.
    if (playback !== this.current) {
      this.leaving.delete(playback);
      this.fading.delete(playback);
      this.release(playback);
      return;
    }
    if (playback.choice.instance === null) {
      const count = this.playable(playback.choice.area).length;
      playback.slot += 1;
      if (playback.slot >= count) {
        playback.slot = 0;
        playback.round += 1;
      }
    }
    void this.startTrack(playback, 60);
  }

  /** Where an instance's timeline says the playing deck should be, or null for personal playback. */
  private expectedOffset(playback: Playback): number | null {
    const instance = playback.choice.instance;
    if (instance === null || playback.trackId === null) return null;
    const placement = this.placement(playback);
    if (placement === null || placement === 'finished' || placement.trackId !== playback.trackId) return null;
    return placement.offsetMs;
  }

  /** A tick: report the position, and keep an instance on the beat. */
  private tick(playback: Playback, deck: Deck): void {
    if (playback.disposed || playback.deck !== deck || playback.loading) return;
    const positionMs = deck.positionMs();
    const expectedMs = this.expectedOffset(playback);
    this.ticked?.({ positionMs, expectedMs, atMs: this.host.now(), durationMs: deck.durationMs });
    if (expectedMs === null) {
      if (playback.choice.instance !== null && playback === this.current) {
        // The timeline has moved on to another track (or finished): follow it — unless this playback is
        // on its way out, which its fade finishes (following it would start the area that was left).
        void this.startTrack(playback, 60);
      }
      return;
    }
    const drift = wrappedDrift(positionMs - expectedMs, playback.choice.area.order === 'loop' ? deck.durationMs : null);
    if (Math.abs(drift) > INSTANCE_RESEEK_MS) {
      deck.seek(expectedMs);
      deck.setRate(1);
    } else if (Math.abs(drift) > INSTANCE_TOLERANCE_MS) {
      // Behind → faster, ahead → slower, proportionally, capped (plan §4: a nudge, not a jump).
      deck.setRate(1 - Math.max(-INSTANCE_MAX_RATE_NUDGE, Math.min(INSTANCE_MAX_RATE_NUDGE, drift / 3_000)));
    } else {
      deck.setRate(1);
    }
  }

  /** A deck's own level: the area's volume times the track's loudness correction. The slider is its bus's. */
  private levelOf(area: AreaDef, trackId: string): number {
    return area.volume * loudnessGain(this.host.manifest[trackId]?.loudnessLufs, loudnessKindOf(trackId));
  }

  private fadeOut(playback: Playback, ms: number): void {
    if (playback.deck === null) {
      this.release(playback);
      return;
    }
    playback.deck.fade(0, ms);
    this.fading.add(playback);
    this.host.setTimer(() => {
      if (this.fading.delete(playback)) this.release(playback);
    }, ms + 250);
  }

  private releaseLater(deck: Deck, ms: number): void {
    this.host.setTimer(() => deck.dispose(), ms);
  }

  private release(playback: Playback): void {
    playback.disposed = true;
    playback.deck?.dispose();
    playback.deck = null;
  }

  private cancelLinger(): void {
    if (this.lingerTimer !== null) {
      this.host.clearTimer(this.lingerTimer);
      this.lingerTimer = null;
    }
  }
}
